import { DreamBlock } from '@typedream/core';
import { findNodePath, toDOMNode } from '@udecode/plate-core';
import { useRef, useState } from 'react';
import { ConnectDropTarget, DropTargetMonitor, useDrop } from 'react-dnd';
import { canInsertBlock, getBlockOptions } from '../../../editorTools/queries';
import { useTPlateEditorRef } from '../../../plate';
import { DragItem, DropDirection } from '../types';

const getDropDirection = (
  monitor: DropTargetMonitor,
  dropTargetElement: HTMLElement,
): DropDirection | void => {
  const clientOffset = monitor.getClientOffset();
  if (!clientOffset) return undefined;

  const dropTargetRect = dropTargetElement.getBoundingClientRect();

  const hoverMiddleY = (dropTargetRect.bottom - dropTargetRect.top) / 2;
  const hoverClientY = clientOffset.y - dropTargetRect.top;

  // use horizontal direction when hovering close to the left or right edges
  // the value is set to min 25% width or max 30px to the left or right
  const leftEdge = dropTargetRect.x + Math.min(30, 0.25 * dropTargetRect.width);
  const rightEdge = dropTargetRect.x
    + Math.max(dropTargetRect.width - 30, 0.75 * dropTargetRect.width);

  if (clientOffset.x < leftEdge) return 'left';
  if (clientOffset.x > rightEdge) return 'right';
  return hoverClientY < hoverMiddleY ? 'top' : 'bottom';
};

export const useDropBlock = (
  element: DreamBlock,
  options: {
    drop: (item: DragItem, direction: DropDirection) => void
  },
): [
  {
    isOver: boolean
    canDrop: boolean
    dropDirection: DropDirection | undefined
  },
  ConnectDropTarget,
] => {
  const editor = useTPlateEditorRef();
  const { isDroppable } = getBlockOptions(editor, element) || {};
  const path = findNodePath(editor, element);
  const [dropDirection, setDropDirection] = useState<DropDirection>();
  const dropDirectionRef = useRef<DropDirection>();

  const [{ isOver, canDrop }, dropTarget] = useDrop(
    {
      accept: 'block',
      collect: (monitor) => {
        const item = monitor.getItem();
        return {
          isOver: monitor.isOver({ shallow: true }),
          canDrop:
            !!isDroppable
            && item
            && item.id !== element.id
            && !!path
            && canInsertBlock(editor, path),
        };
      },
      canDrop: (item: DragItem) => !!isDroppable
        && item
        && item.id !== element.id
        && !!path
        && canInsertBlock(editor, path),
      drop: (item: DragItem, monitor) => {
        if (monitor.didDrop()) return;
        options.drop(item, dropDirectionRef.current || 'bottom');
      },
      hover: (_, monitor) => {
        const domElement = toDOMNode(editor, element);
        if (!domElement) return;

        const dir = getDropDirection(monitor, domElement);
        if (!dir) return;

        setDropDirection(dir);
        dropDirectionRef.current = dir;
      },
    },
    [element],
  );

  return [{ isOver, canDrop, dropDirection }, dropTarget];
};
